import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { StockIssue } from '../../core/models/inventory.models';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import {
  currentTimestamp,
  formatCurrency,
  formatDate,
} from '../../core/utils/format.util';
import {
  defaultBranchFilter,
  isBranchFilterLocked,
  matchesBranchFilter,
  resetBranchFilter,
} from '../../core/utils/branch-scope.util';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-issued-stock',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './issued-stock.component.html',
  styleUrls: ['./issued-stock.component.css'],
})
export class IssuedStockComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);

  readonly timestamp = currentTimestamp;
  readonly formatCurrency = formatCurrency;
  readonly formatDate = formatDate;
  readonly branches = this.settings.scopedBranches;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));

  readonly search = signal('');
  readonly branchFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 12;

  readonly showEditModal = signal(false);
  readonly editingIssue = signal<StockIssue | null>(null);
  readonly editIssuedTo = signal('');
  readonly editIssueDate = signal('');
  readonly editStatus = signal<StockIssue['status']>('issued');
  readonly saving = signal(false);

  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  readonly stats = computed(() => {
    const rows = this.inventory.issueStockList();
    return {
      total: rows.length,
      issued: rows.filter((r) => r.status === 'issued').length,
      pending: rows.filter(
        (r) => r.status === 'pending' || r.status === 'reserved',
      ).length,
      cancelled: 0,
      totalQty: rows.reduce((sum, r) => sum + (r.items || 0), 0),
      totalValue: rows.reduce((sum, r) => sum + (r.totalValue || 0), 0),
    };
  });

  readonly filteredIssues = computed(() => {
    const search = this.search().trim().toLowerCase();
    const branch = this.branchFilter();

    return this.inventory.issueStockList().filter((issue) => {
      const matchesSearch =
        !search ||
        issue.issueNumber.toLowerCase().includes(search) ||
        issue.issuedTo.toLowerCase().includes(search) ||
        (issue.recipientPhone ?? '').toLowerCase().includes(search) ||
        (issue.recipientLocation ?? '').toLowerCase().includes(search) ||
        (issue.itemName ?? '').toLowerCase().includes(search) ||
        (issue.branch ?? '').toLowerCase().includes(search);

      const matchesBranch = matchesBranchFilter(
        branch,
        issue.branch,
        issue.branchId,
        this.branches(),
      );
      return matchesSearch && matchesBranch;
    });
  });

  readonly paginatedIssues = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredIssues().slice(start, start + this.pageSize);
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredIssues().length / this.pageSize)),
  );

  readonly paginationStart = computed(() =>
    this.filteredIssues().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );

  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredIssues().length),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.refreshFromApi();
    }
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  clearFilters(): void {
    this.search.set('');
    this.branchFilter.set(resetBranchFilter(this.auth, this.branches()));
    this.page.set(1);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.page.set(page);
    }
  }

  statusLabel(status: StockIssue['status']): string {
    const map: Record<StockIssue['status'], string> = {
      pending: 'Pending',
      reserved: 'Reserved',
      issued: 'Issued',
      cancelled: 'Cancelled',
    };
    return map[status];
  }

  historyItemId(issue: StockIssue): string | null {
    if (issue.inventoryItemId && Number(issue.inventoryItemId) > 0) {
      return issue.inventoryItemId;
    }
    const firstName = (issue.itemName || '').split(',')[0].trim().toLowerCase();
    if (!firstName) {
      return null;
    }
    const match = this.inventory
      .allItems()
      .find((item) => item.name.toLowerCase() === firstName);
    return match?.id ?? null;
  }

  openEditModal(issue: StockIssue): void {
    this.editingIssue.set(issue);
    this.editIssuedTo.set(issue.issuedTo);
    this.editIssueDate.set(issue.issueDate);
    this.editStatus.set(issue.status);
    this.showEditModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeEditModal(): void {
    this.showEditModal.set(false);
    this.editingIssue.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeEditModal();
    }
  }

  saveIssue(): void {
    const issue = this.editingIssue();
    if (!issue) {
      return;
    }

    const issuedTo = this.editIssuedTo().trim();
    if (!issuedTo || !this.editIssueDate()) {
      this.showToastMessage('Issued To and Date are required', 'error');
      return;
    }

    if (issue.status === 'issued' && this.editStatus() !== 'issued') {
      this.showToastMessage(
        'This stock has been issued. The status cannot be changed.',
        'error',
      );
      this.editStatus.set('issued');
      return;
    }

    const status = issue.status === 'issued' ? 'issued' : this.editStatus();
    this.saving.set(true);
    this.inventory
      .updateIssueStockViaApi(issue.id, {
        issuedTo,
        issueDate: this.editIssueDate(),
        status: status === 'reserved' ? 'reserved' : status,
      })
      .subscribe((result) => {
        this.saving.set(false);
        if (result.success) {
          this.closeEditModal();
          this.showToastMessage(
            `Issue ${issue.issueNumber} updated to ${this.statusLabel(status === 'reserved' ? 'reserved' : status)}`,
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to update issue',
            'error',
          );
        }
      });
  }

  quickSetStatus(issue: StockIssue, status: StockIssue['status']): void {
    if (issue.status === 'issued') {
      this.showToastMessage(
        'This stock has been issued. The status cannot be changed.',
        'error',
      );
      return;
    }
    if (issue.status === status) {
      return;
    }

    const nextStatus = status === 'reserved' ? 'issued' : status;
    this.inventory
      .updateIssueStockViaApi(issue.id, {
        issuedTo: issue.issuedTo,
        issueDate: issue.issueDate,
        status:
          nextStatus === 'issued' && issue.status === 'reserved'
            ? 'issued'
            : nextStatus,
      })
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            issue.status === 'reserved' && nextStatus === 'issued'
              ? `Reserved stock ${issue.issueNumber} issued`
              : `Issue ${issue.issueNumber} marked as ${this.statusLabel(nextStatus)}`,
            'success',
          );
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to update status',
            'error',
          );
        }
      });
  }

  private showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  exportData(): void {
    const rows = this.filteredIssues().map((issue) => ({
      'Issue No.': issue.issueNumber,
      'Issued To': issue.issuedTo,
      Phone: issue.recipientPhone ?? '',
      Destination: issue.recipientLocation ?? '',
      Item: issue.itemName ?? '',
      Branch: issue.branch,
      Qty: issue.items,
      'Total Value': issue.totalValue ?? 0,
      Date: issue.issueDate,
      Status: this.statusLabel(issue.status),
    }));

    if (!rows.length) {
      this.showToastMessage(
        'No issued stock matches the current filters.',
        'info',
      );
      return;
    }

    this.exporter.downloadCsv(
      `issued-stock-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }
}
